import React, { useState } from 'react';
import { ArrowLeft, ChevronDown, ChevronUp, Download } from 'lucide-react';
import ACGMEIcon from '../shared/icons/ACGMEIcon';

const ACGMEMilestonesPage = ({ onBack }) => {
  const [expandedDomains, setExpandedDomains] = useState({
    'patient-care': false,
    'medical-knowledge': false,
    'systems-based': false,
    'practice-based': false,
    'professionalism': false,
    'interpersonal': false
  });

  // State to track which level is selected for each subcompetency
  // Key is domain-id + subcomp index, value is selected level (1-5)
  const [selectedLevels, setSelectedLevels] = useState({});

  // State to track animation direction for each subcompetency
  const [animationKeys, setAnimationKeys] = useState({});

  const toggleDomain = (domain) => {
    setExpandedDomains(prev => ({
      ...prev,
      [domain]: !prev[domain]
    }));
  };

  const getSelectedLevel = (domainId, subcompIndex) => {
    const key = `${domainId}-${subcompIndex}`;
    return selectedLevels[key] || 1; // Default to Level 1
  };

  const setSelectedLevel = (domainId, subcompIndex, level) => {
    const key = `${domainId}-${subcompIndex}`;
    setSelectedLevels(prev => ({
      ...prev,
      [key]: level
    }));
    // Trigger animation by updating key
    setAnimationKeys(prev => ({
      ...prev,
      [key]: Date.now()
    }));
  };

  const competencyDomains = [
    {
      id: 'patient-care',
      name: 'Patient Care',
      color: 'from-blue-500 to-blue-600',
      borderColor: 'border-blue-200',
      bgColor: 'bg-blue-50',
      subcompetencies: [
        {
          name: 'Pre-Anesthetic Evaluation',
          levels: [
            ['Performs basic chart review', 'Conducts patient interview, with direct supervision', 'Conducts and interprets a physical examination, with direct supervision'],
            ['Performs focused chart review, with indirect supervision', 'Interviews the patient and gathers pertinent information, with indirect supervision', 'Conducts a focused physical examination, with indirect supervision'],
            ['Interprets chart review information to assess need for further work-up', 'Interprets information collected during patient interview, with assistance', 'Identifies comorbidities on physical examination that may require further evaluation, with indirect supervision'],
            ['Evaluates diagnostic data and provides risk stratification based on comorbidities and anesthetic implications', 'Independently identifies the need for additional evaluation and suggests therapeutic interventions', 'Independently identifies concerning physical exam findings that require further evaluation'],
            ['Independently identifies a previously undiagnosed condition']
          ]
        },
        {
          name: 'Peri-Operative Care and Management',
          levels: [
            ['Identifies the components of an anesthetic plan', 'Identifies the components of a pain management plan', 'Identifies potential impact of anesthesia beyond intra-operative period'],
            ['Develops an anesthetic plan for a healthy patient undergoing uncomplicated procedures', 'Implements simple peri-operative pain management plan', 'Identifies patient specific risks factors for long-term anesthetic effects'],
            ['Develops an anesthetic plan for patients with well-controlled comorbidities or undergoing complicated procedures', 'Identifies patients with a history of chronic pain who require a modified peri-operative pain management plan', 'Develops the anesthetic plan based on risk factors to mitigate the long-term impact of anesthesia'],
            ['Develops an anesthetic plan for patients with multiple, uncontrolled comorbidities, and undergoing complicated procedures', 'Implements the anesthetic plan for patients with complex pain history and polypharmacy', 'Implements the anesthetic plan to mitigate the long-term impact of anesthesia'],
            ['In collaboration with other specialists, develops protocols for multimodal analgesia plan for patients with a complex pain history and substance use disorder', 'Develops departmental or institutional protocols for reduction of the long-term impact of anesthesia']
          ]
        },
        {
          name: 'Application and Interpretation of Monitors',
          levels: [
            ['Identifies standard monitors', 'Applies standard monitors to patients', 'Interprets standard monitoring data'],
            ['Independently selects central and arterial catheters based on patient comorbidities and procedure', 'Inserts central and arterial catheters, with supervision', 'Addresses malfunctions in standard monitors and interprets data from central and arterial lines, with supervision'],
            ['Selects advanced monitors based on patient comorbidities and procedure, with supervision', 'Inserts or applies advanced monitors, with supervision', 'Recognizes and addresses malfunctions in advanced monitors and interprets data, with supervision'],
            ['Independently selects advanced monitors based on patient comorbidities and procedure', 'Independently inserts or applies advanced invasive monitors', 'Independently interprets data, recognizes, and addresses malfunctions in monitors and other anesthesia equipment'],
            ['Functions as a consultant for difficult advanced monitor placement', 'Participates in the research and/or development of protocols for monitoring technology']
          ]
        },
        {
          name: 'Intra-Operative Care',
          levels: [
            ['Assists in the initiation of the anesthetic', 'Assists in maintenance of anesthetic care', 'Assists with emergence from anesthesia'],
            ['Plans and initiates the anesthetic for healthy patients undergoing uncomplicated procedures', 'Manages expected events during anesthetic care, with supervision', 'Anticipates and manages expected events during emergence, with supervision'],
            ['Plans and initiates the anesthetic in a patient with well-controlled comorbidities, or undergoing complicated procedures', 'Independently manages expected events during anesthetic care', 'Anticipates and manages unexpected events during emergence, with supervision'],
            ['Independently plans and initiates the anesthetic in a patient with multiple, uncontrolled comorbidities undergoing complicated procedures', 'Independently manages unexpected events during anesthetic care', 'Independently anticipates and manages unexpected events during emergence'],
            ['Manages rare events during anesthetic care', 'Manages rare events during emergence']
          ]
        },
        {
          name: 'Airway Management',
          levels: [
            ['Performs basic airway assessment', 'Performs bag-mask ventilation in uncomplicated airway'],
            ['Uses the airway exam and identifiable risk factors to formulate a patient-specific plan', 'Prepares basic equipment and manages an uncomplicated airway'],
            ['Devises airway management plans that address contingencies, with supervision', 'Prepares and incorporates advanced equipment in the management of a complicated airway, with supervision'],
            ['Independently devises airway management plans that address contingencies', 'Independently prepares and incorporates advanced equipment in the management of a complicated airway'],
            ['Functions as an expert in an airway crisis for complicated airways']
          ]
        },
        {
          name: 'Point-of-Care Ultrasound',
          levels: [
            ['Lists and explains the basic science and terminology of ultrasound', 'Identifies relevant anatomy using ultrasound', 'Uses ultrasound for vascular access in routine situations, with supervision'],
            ['Selects ultrasound equipment for procedures, with supervision', 'Conducts point-of-care ultrasound, with supervision', 'Uses ultrasound for vascular access in routine situations'],
            ['Selects ultrasound equipment for a patient with difficult anatomy, with supervision', 'Interprets point-of-care ultrasound, with supervision', 'Uses ultrasound for vascular access in complex situations, with supervision'],
            ['Independently selects proper ultrasound equipment and settings for indicated scenarios', 'Independently conducts and interprets point-of-care ultrasound', 'Independently uses ultrasound for vascular access in complex situations'],
            ['Participates in research of emerging ultrasound procedures', 'Participates in the development of institutional protocols for point-of-care ultrasound']
          ]
        },
        {
          name: 'Situational Awareness and Crisis Management',
          levels: [
            ['Demonstrates vigilance during clinical care', 'Articulates causes of common peri-operative crisis situations', 'Responds to crisis situations as a reliable team member'],
            ['Demonstrates awareness of case flow and developments throughout a procedure', 'Recognizes crisis situations; calls for help', 'Participates in management during crisis situations'],
            ['Demonstrates awareness of case flow and developments throughout a procedure, including those outside of one\'s own immediate control, with supervision', 'Anticipates impending crisis and identifies possible etiologies with supervision', 'Initiates management and resolves crisis situations, with supervision'],
            ['Independently demonstrates awareness of case flow and developments throughout a procedure, including those outside of one\'s own immediate control', 'Independently anticipates impending crisis and identifies possible etiologies', 'Independently initiates management and resolves crisis situations'],
            ['Leads the health care team in the management of crisis situations']
          ]
        },
        {
          name: 'Post-Operative Care',
          levels: [
            ['Outlines post-operative disposition options for patients', 'Lists complications commonly encountered post-operatively'],
            ['Plans disposition for uncomplicated procedures', 'Diagnoses, manages, and documents commonly encountered complications arising from anesthetic care, with supervision'],
            ['Identifies unexpected changes in patient status meriting change in disposition, with supervision', 'Diagnoses, manages, and documents uncommon complications arising from anesthetic care, with supervision'],
            ['Independently identifies unexpected changes in patient status meriting change in disposition', 'Independently diagnoses, manages, and documents uncommon complications arising from anesthetic care'],
            ['Develops protocols for disposition based on procedure and patient comorbidities']
          ]
        },
        {
          name: 'Critical Care',
          levels: [
            ['Acquires data for the care of the critically-ill patient', 'Recognizes when a patient is critically ill'],
            ['Interprets routine diagnostic data in the care of critically-ill patients', 'Prioritizes the care of the critically-ill patient', 'Implements the care team\'s plan for a critically-ill patient'],
            ['Interprets advanced diagnostic data in the care of critically-ill patients, with supervision', 'Prioritizes the care of multiple critically-ill patients, with supervision', 'Develops and implements a comprehensive plan of care for the critically-ill patient, with supervision'],
            ['Independently interprets advanced diagnostic data in the care of critically-ill patients', 'Independently prioritizes the care of multiple critically-ill patients', 'Develops and implements a comprehensive plan of care for the critically-ill patient'],
            ['Leads and deploys resources in the care of the critically-ill patient', 'Functions in a supervisory role managing all patients in a unit and the unit\'s resources']
          ]
        },
        {
          name: 'Regional Anesthesia',
          levels: [
            ['Describes anatomy relevant to regional anesthesia', 'Prepares the patient and the equipment for common regional anesthesia techniques', 'Describes potential complications of regional anesthesia'],
            ['Describes indications and contraindications for regional anesthesia', 'Performs regional anesthesia techniques, with direct supervision', 'Recognizes and manages complications of regional anesthesia, with direct supervision'],
            ['Develops a patient- and procedure-specific regional anesthesia plan, with supervision', 'Performs regional anesthesia techniques, with indirect supervision', 'Recognizes and manages complications of regional anesthesia, with indirect supervision'],
            ['Independently develops a patient- and procedure-specific regional anesthesia plan', 'Independently performs regional anesthesia techniques', 'Independently recognizes and manages complications of regional anesthesia'],
            ['Serves as a consultant on advanced or difficult regional techniques', 'Develops institutional protocol for using regional anesthesia and managing complications']
          ]
        }
      ]
    },
    {
      id: 'medical-knowledge',
      name: 'Medical Knowledge',
      color: 'from-green-500 to-green-600',
      borderColor: 'border-green-200',
      bgColor: 'bg-green-50',
      subcompetencies: [
        {
          name: 'Foundational Knowledge',
          levels: [
            ['Demonstrates knowledge of pathophysiology and treatment of medical and surgical conditions', 'Identifies medications used to treat common comorbidities'],
            ['Demonstrates knowledge of common medical and surgical disease, treatments, and populations as it relates to anesthetic care', 'Demonstrates knowledge of pharmacology of medications routinely used in anesthetic care'],
            ['Demonstrates knowledge of complex medical and surgical disease, treatments, and populations as it relates to anesthetic care', 'Demonstrates knowledge of medications used in subspecialty areas (e.g., cardiac, obstetrics)'],
            ['Demonstrates comprehensive knowledge of medical and surgical disease as it relates to the full spectrum of the patient\'s peri-operative care', 'Demonstrates comprehensive knowledge of pharmacology in the setting of complex comorbidities'],
            ['Demonstrates scientific knowledge of uncommon, atypical, or complex conditions as it relates to the full spectrum of the patient\'s peri-operative care', 'Participates in research related to pharmacology']
          ]
        },
        {
          name: 'Clinical Reasoning',
          levels: [
            ['Organizes and accurately summarizes information obtained from the patient evaluation to develop a clinical impression', 'Lists types of clinical reasoning errors'],
            ['Integrates information from all sources to develop a basic differential diagnosis for common patient presentations', 'Identifies clinical reasoning errors within patient care, with guidance'],
            ['Develops a thorough and prioritized differential diagnosis for common patient presentations', 'Retrospectively applies clinical reasoning principles to identify errors'],
            ['Develops prioritized differential diagnoses in complex patient presentations and incorporates subtle, unusual, or conflicting findings', 'Continually re-appraises one\'s clinical reasoning to improve patient care in real time'],
            ['Coaches others to develop prioritized differential diagnoses in complex patient presentations', 'Models how to recognize errors and reflect upon one\'s own clinical reasoning']
          ]
        }
      ]
    },
    {
      id: 'systems-based',
      name: 'Systems-Based Practice',
      color: 'from-purple-500 to-purple-600',
      borderColor: 'border-purple-200',
      bgColor: 'bg-purple-50',
      subcompetencies: [
        {
          name: 'Patient Safety and Quality Improvement',
          levels: [
            ['Demonstrates knowledge of common events that impact patient safety', 'Demonstrates knowledge of how to report patient safety events', 'Demonstrates knowledge of basic quality improvement methodologies and metrics'],
            ['Identifies system factors that lead to patient safety events', 'Reports patient safety events through institutional reporting systems (simulated or actual)', 'Describes departmental quality improvement initiatives'],
            ['Participates in analysis of patient safety events (simulated or actual)', 'Participates in disclosure of patient safety events to patients and families (simulated or actual)', 'Participates in department quality improvement initiatives'],
            ['Conducts analysis of patient safety events and offers error prevention strategies (simulated or actual)', 'Discloses patient safety events to patients and families (simulated or actual)', 'Demonstrates the skills required to identify, develop, implement, and analyze a quality improvement project'],
            ['Actively engages teams and processes to modify systems to prevent patient safety events', 'Role models or mentors others in the disclosure of patient safety events', 'Creates, implements, and assesses quality improvement initiatives at the institutional level or above']
          ]
        },
        {
          name: 'System Navigation for Patient-Centered Care',
          levels: [
            ['Demonstrates knowledge of care coordination', 'Identifies key elements for safe and effective transitions of care and hand-offs', 'Demonstrates knowledge of population and community health needs'],
            ['Coordinates care of patients in routine clinical situations effectively using the roles of the interprofessional team members', 'Performs safe and effective transitions of care/hand-offs in routine clinical situations', 'Identifies specific population and community health needs for their local population'],
            ['Coordinates care of patients in complex clinical situations effectively using the roles of the interprofessional team members', 'Performs safe and effective transitions of care/hand-offs in complex clinical situations', 'Uses institutional resources effectively to meet the needs of a patient population and community'],
            ['Role models effective coordination of patient-centered care among different disciplines and specialties', 'Role models and advocates for safe and effective transitions of care/hand-offs within and across health care delivery systems', 'Participates in changing and adapting practice to provide for the needs of specific populations'],
            ['Analyzes the process of care coordination and participates in the design and implementation of improvements', 'Improves quality of transitions of care within and across health care delivery systems to optimize patient outcomes', 'Advocates for populations and communities with specific health care needs in the peri-operative setting']
          ]
        },
        {
          name: 'Physician Role in Health Care Systems',
          levels: [
            ['Identifies key components of the complex health care system (e.g., hospital, skilled nursing facility, finance, personnel, technology)', 'States factors impacting the costs of anesthetic care'],
            ['Describes how components of a complex health care system are interrelated, and how this impacts patient care', 'Documents anesthetic detail to facilitate accurate billing and reimbursement'],
            ['Discusses how individual practice affects the broader system (e.g., length of stay, readmission rates, clinical efficiency)', 'Explains the impact of documentation on billing and reimbursement'],
            ['Manages various components of the complex health care system to provide efficient and effective patient care and transition of care', 'Practices and advocates for cost-effective patient care'],
            ['Advocates for or leads systems change that enhances high-value, efficient, and effective patient care', 'Engages in external activities related to advocacy for cost-effective care']
          ]
        }
      ]
    },
    {
      id: 'practice-based',
      name: 'Practice-Based Learning and Improvement',
      color: 'from-amber-500 to-amber-600',
      borderColor: 'border-amber-200',
      bgColor: 'bg-amber-50',
      subcompetencies: [
        {
          name: 'Evidence-Based and Informed Practice',
          levels: [
            ['Accesses and uses evidence in routine patient care'],
            ['Articulates clinical questions and elicits patient preferences and values to guide evidence-based care'],
            ['Locates and applies the best available evidence, integrated with patient preference, to the care of complex patients'],
            ['Appraises and applies evidence, even in the face of uncertainty and conflicting evidence, to guide individualized care'],
            ['Coaches others to appraise and apply evidence for complex patients and/or participates in the development of guidelines']
          ]
        },
        {
          name: 'Reflective Practice and Commitment to Personal Growth',
          levels: [
            ['Accepts responsibility for personal and professional development by establishing goals', 'Identifies the factors which contribute to performance deficits', 'Actively seeks opportunities to improve'],
            ['Demonstrates openness to performance data (feedback and other input) in order to form goals', 'Analyzes and acknowledges the factors which contribute to performance deficits', 'Designs and implements a learning plan, with prompting'],
            ['Seeks performance data episodically, with adaptability and humility', 'Institutes behavioral change(s) to improve performance', 'Independently creates and implements a learning plan'],
            ['Intentionally seeks performance data consistently, with adaptability and humility', 'Considers alternatives to improve performance', 'Integrates performance data to adapt the learning plan'],
            ['Role models consistently seeking performance data, with adaptability and humility', 'Models reflective practice', 'Facilitates the design and implementation of learning plans for others']
          ]
        }
      ]
    },
    {
      id: 'professionalism',
      name: 'Professionalism',
      color: 'from-sky-500 to-sky-600',
      borderColor: 'border-sky-200',
      bgColor: 'bg-sky-50',
      subcompetencies: [
        {
          name: 'Professional Behavior and Ethical Principles',
          levels: [
            ['Identifies potential triggers for professionalism lapses', 'Describes when and how to report lapses in professionalism', 'Demonstrates knowledge of the ethical principles underlying patient care'],
            ['Demonstrates insight into professional behavior in routine situations', 'Takes responsibility for one\'s own professionalism lapses', 'Analyzes straightforward situations using ethical principles'],
            ['Demonstrates professional behavior in complex or stressful situations', 'Recognizes need to seek help in managing and resolving complex interpersonal situations', 'Analyzes complex situations using ethical principles'],
            ['Recognizes situations that may trigger professionalism lapses and intervenes to prevent lapses in oneself', 'Actively solicits help and acts on recommendations to resolve complex interpersonal situations', 'Recognizes and utilizes resources for managing and resolving ethical dilemmas'],
            ['Coaches others when their behavior fails to meet professional expectations', 'Identifies and seeks to address system-level factors that induce or exacerbate ethical problems or impede their resolution']
          ]
        },
        {
          name: 'Accountability/Conscientiousness',
          levels: [
            ['Responds promptly to requests or reminders to complete tasks', 'Takes responsibility for failure to complete tasks'],
            ['Performs tasks and responsibilities in a timely manner', 'Recognizes situations that may impact one\'s own ability to complete tasks and responsibilities in a timely manner'],
            ['Performs tasks and responsibilities in a timely manner with appropriate attention to detail in routine situations', 'Takes responsibility for tasks not completed in a timely manner and identifies strategies to prevent recurrence'],
            ['Prioritizes tasks and responsibilities in a timely manner with appropriate attention to detail in complex or stressful situations', 'Proactively implements strategies to ensure that the needs of patients, teams, and systems are met'],
            ['Designs and implements an institutional systems approach to ensure timely task completion and shared responsibility']
          ]
        },
        {
          name: 'Well-Being',
          levels: [
            ['Recognizes the importance of addressing personal and professional well-being', 'Describes institutional resources that are meant to promote well-being'],
            ['Lists available resources for personal and professional well-being', 'Describes institutional resources that are meant to promote well-being'],
            ['With assistance, proposes a plan to promote personal and professional well-being', 'Recognizes which institutional factors affect well-being'],
            ['Independently develops a plan to promote personal and professional well-being', 'Describes institutional factors that positively and/or negatively affect well-being'],
            ['Creates institutional-level interventions that promote colleagues\' well-being', 'Describes institutional programs designed to examine systemic contributors to burnout']
          ]
        }
      ]
    },
    {
      id: 'interpersonal',
      name: 'Interpersonal and Communication Skills',
      color: 'from-rose-500 to-rose-600',
      borderColor: 'border-rose-200',
      bgColor: 'bg-rose-50',
      subcompetencies: [
        {
          name: 'Patient- and Family-Centered Communication',
          levels: [
            ['Communicates with patients and their families in an understandable and respectful manner', 'Provides timely updates to patients and patients\' families'],
            ['Customizes communication in the setting of biases and barriers with patients and patients\' families', 'Actively listens to patients and patients\' families to elicit patient preferences and expectations'],
            ['Explains complex and difficult information to patients and patients\' families', 'Uses shared decision making to make a personalized care plan'],
            ['Facilitates difficult discussions with patients and patients\' families', 'Effectively negotiates and manages conflict among patients, patients\' families, and the health care team'],
            ['Mentors others in the facilitation of crucial conversations', 'Mentors others in conflict resolution']
          ]
        },
        {
          name: 'Interprofessional and Team Communication',
          levels: [
            ['Respectfully requests or receives consultations', 'Uses language that values all members of the health care team', 'Respectfully receives feedback from the health care team'],
            ['Clearly, concisely and promptly requests or responds to a consultation', 'Communicates information effectively with all health care team members', 'Solicits feedback on performance as a member of the health care team'],
            ['Uses closed-loop communication to verify understanding', 'Adapts communication style to fit team needs', 'Communicates concerns and provides feedback to peers and learners'],
            ['Coordinates recommendations from different members of the health care team to optimize patient care', 'Maintains effective communication in crisis situations', 'Communicates constructive feedback to superiors'],
            ['Role models flexible communication strategies that value input from all health care team members, resolving conflict when needed', 'Leads an after-event debrief of the health care team', 'Facilitates regular health care team-based feedback in complex situations']
          ]
        },
        {
          name: 'Communication within Health Care Systems',
          levels: [
            ['Accurately records information in the patient record; demonstrates judicious use of documentation shortcuts', 'Safeguards patient personal health information', 'Communicates through appropriate channels as required by institutional policy'],
            ['Accurately records information in the anesthetic record for basic cases', 'Documents required data in formats specified by institutional policy', 'Respectfully communicates concerns about the system'],
            ['Accurately records information in the anesthetic record and communicates complex care decisions for complex cases', 'Appropriately selects direct and indirect forms of communication based on context', 'Respectfully communicates concerns about the system and contributes to solutions'],
            ['Uses medical record functionality to highlight challenges in anesthetic care to facilitate future peri-operative management', 'Models exemplary written or verbal communication', 'Uses appropriate channels to offer clear and constructive suggestions to improve the system'],
            ['Explores innovative uses of the medical record to facilitate peri-operative management', 'Guides departmental or institutional policies and procedures around communication', 'Initiates difficult conversations with appropriate stakeholders to improve the system']
          ]
        }
      ]
    }
  ];

  return (
    <div className="max-w-md mx-auto bg-gray-50 min-h-screen pb-6">
      {/* Add keyframe animation */}
      <style>{`
        @keyframes slideIn {
          from {
            opacity: 0;
            transform: translateX(20px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }
      `}</style>

      {/* Header */}
      <div className="bg-gradient-to-r from-slate-800 to-slate-700 text-white p-6 pb-8">
        <button onClick={() => { onBack(); }} className="flex items-center gap-2 text-white mb-4 hover:opacity-80">
          <ArrowLeft size={20} />
          <span>Back to Residency</span>
        </button>

        <div className="flex items-center gap-4">
          <div className="w-14 h-14 bg-white border-2 border-red-600 rounded-xl flex items-center justify-center shadow">
            <ACGMEIcon size={32} className="text-red-600" />
          </div>
          <div>
            <h1 className="font-merriweather text-2xl font-bold">ACGME Milestones</h1>
            <p className="text-slate-200 text-sm mt-1">Anesthesiology Training Progression</p>
          </div>
        </div>
      </div>

      {/* Introduction Card */}
      <div className="px-6 mt-6">
        <div className="bg-sky-50 border-2 border-sky-200 rounded-xl p-4">
          <h3 className="font-bold text-sky-800 mb-2 flex items-center gap-2">
            <div className="w-7 h-7 bg-white border border-red-600 rounded-lg flex items-center justify-center">
              <ACGMEIcon size={18} className="text-red-600" />
            </div>
            About ACGME Milestones
          </h3>
          <p className="text-gray-700 text-sm leading-relaxed mb-3">
            These milestones represent the evaluation criteria used by the BCM Anesthesia Residency Clinical Competency Committee (CCC)
            to assess resident progress throughout training. The milestones are organized into 6 core competency
            domains with 23 specific subcompetencies. Milestones are primarily designed for formative, developmental
            purposes to support continuous quality improvement.
          </p>
          <div className="bg-white border-2 border-sky-100 rounded-lg p-3 mt-3">
            <h4 className="font-semibold text-gray-900 text-sm mb-2">5-Level Progression System:</h4>
            <div className="space-y-1 text-xs text-gray-700">
              <div className="flex items-start gap-2">
                <span className="font-semibold text-sky-700 min-w-[60px]">Level 1:</span>
                <span>Novice - Requires direct supervision</span>
              </div>
              <div className="flex items-start gap-2">
                <span className="font-semibold text-sky-700 min-w-[60px]">Level 2:</span>
                <span>Advanced Beginner - Requires indirect supervision (Avg CA-1)</span>
              </div>
              <div className="flex items-start gap-2">
                <span className="font-semibold text-sky-700 min-w-[60px]">Level 3:</span>
                <span>Competent - Can function independently (Avg CA-2)</span>
              </div>
              <div className="flex items-start gap-2">
                <span className="font-semibold text-sky-700 min-w-[60px]">Level 4:</span>
                <span>Proficient - Graduation goal (Avg CA-3)</span>
              </div>
              <div className="flex items-start gap-2">
                <span className="font-semibold text-sky-700 min-w-[60px]">Level 5:</span>
                <span>Expert - Achievements greater than expectation</span>
              </div>
            </div>
          </div>
          <div className="bg-amber-50 border-2 border-amber-200 rounded-lg p-3 mt-3">
            <p className="text-xs text-gray-700 leading-relaxed">
              <span className="font-semibold text-amber-800">Important:</span> Level 4 is designed as a graduation goal
              but does not represent a graduation requirement. The program director determines readiness for graduation
              and unsupervised practice. Milestones are not used as the sole assessment in high-stakes decisions
              such as certification or credentialing.
            </p>
          </div>
        </div>
      </div>

      {/* Competency Domains */}
      <div className="px-6 mt-6 space-y-3">
        <h2 className="text-lg font-bold text-gray-900">Competency Domains</h2>

        {competencyDomains.map((domain) => {
          const isExpanded = expandedDomains[domain.id];

          return (
            <div key={domain.id} className="bg-white rounded-xl border border-gray-200 overflow-hidden">
              <div
                className={`bg-gradient-to-r ${domain.color} text-white px-4 py-3 cursor-pointer hover:opacity-90 transition-opacity`}
                onClick={() => toggleDomain(domain.id)}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="font-bold">{domain.name}</h3>
                    <p className="text-xs text-white/80 mt-0.5">
                      {domain.subcompetencies.length} subcompetencies
                    </p>
                  </div>
                  {isExpanded ? (
                    <ChevronUp size={20} className="text-white" />
                  ) : (
                    <ChevronDown size={20} className="text-white" />
                  )}
                </div>
              </div>

              {isExpanded && (
                <div className="p-4 space-y-4">
                  {domain.subcompetencies.map((subcomp, idx) => {
                    const selectedLevel = getSelectedLevel(domain.id, idx);
                    const animationKey = animationKeys[`${domain.id}-${idx}`] || 0;

                    return (
                      <div key={idx} className={`border-2 ${domain.borderColor} rounded-lg p-3 ${domain.bgColor}`}>
                        <h4 className="font-semibold text-gray-900 text-sm mb-4">{subcomp.name}</h4>

                        {/* Interactive Level Selector */}
                        <div className="mb-4">
                          <div className="flex items-center justify-between relative">
                            {/* Connecting Line */}
                            <div className="absolute top-4 left-0 right-0 h-1 bg-gradient-to-r from-gray-300 via-sky-400 to-emerald-500"></div>

                            {/* Level Circles */}
                            {[1, 2, 3, 4, 5].map((level) => (
                              <div key={level} className="flex flex-col items-center relative z-10">
                                {/* Clickable Circle */}
                                <button
                                  onClick={() => setSelectedLevel(domain.id, idx, level)}
                                  className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold shadow-md transition-all ${
                                    selectedLevel === level
                                      ? 'ring-2 ring-offset-2 ring-slate-800 scale-110'
                                      : 'hover:scale-105'
                                  } ${
                                    level === 1 ? 'bg-gray-300 text-gray-700' :
                                    level === 2 ? 'bg-sky-300 text-sky-800' :
                                    level === 3 ? 'bg-sky-400 text-sky-900' :
                                    level === 4 ? 'bg-emerald-400 text-emerald-900' :
                                    'bg-emerald-500 text-white'
                                  }`}
                                >
                                  {level}
                                </button>
                              </div>
                            ))}
                          </div>
                        </div>

                        {/* Level Content with Slide Animation */}
                        <div className="mt-2 overflow-hidden relative">
                          <div
                            key={animationKey}
                            className="animate-slideIn"
                            style={{
                              animation: 'slideIn 0.3s ease-out'
                            }}
                          >
                            <div className="bg-white border border-gray-200 rounded-lg p-3">
                              <div className="flex items-center gap-2 mb-2">
                                <span className="font-bold text-sm text-slate-800">Level {selectedLevel}</span>
                                <span className="text-xs text-gray-600">
                                  {selectedLevel === 1 ? '(Novice)' :
                                   selectedLevel === 2 ? '(Advanced Beginner)' :
                                   selectedLevel === 3 ? '(Competent)' :
                                   selectedLevel === 4 ? '(Proficient - Graduation Goal)' :
                                   '(Expert)'}
                                </span>
                              </div>
                              <ul className="space-y-2 text-sm text-gray-700">
                                {subcomp.levels[selectedLevel - 1].map((item, bulletIdx) => (
                                  <li key={bulletIdx} className="flex items-start gap-2">
                                    <span className="text-sky-600 mt-1">•</span>
                                    <span>{item}</span>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Download PDF Section */}
      <div className="px-6 mt-6">
        <a
          href="/anes-dept/acgme-milestones.pdf"
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center justify-center gap-3 bg-slate-800 text-white rounded-xl p-4 hover:bg-slate-700 transition-colors"
        >
          <Download size={20} />
          <span className="font-semibold">Download Official PDF</span>
        </a>
      </div>
    </div>
  );
};

export default ACGMEMilestonesPage;
